import { MoreOutlined } from "@ant-design/icons";
import {
  Button,
  Dropdown,
  MenuProps,
  Space,
  TablePaginationConfig,
} from "antd";
import { FilterValue, SorterResult } from "antd/es/table/interface";
import { Column, RecordCountBadge, Table } from "components";
import {
  StyledTableHeader,
  StyledTableWrapper,
} from "components/table/table.styled";
import { usePermissions, useUserContext } from "features/users";
import { CreateApprovedDTO } from "features/withdraw/api/withdraw-api.service";
import { ApproveModal } from "features/withdraw/components/modal/ConfirmModal";
import { RefundModal } from "features/withdraw/components/modal/RefundModal";
import { RejectModal } from "features/withdraw/components/modal/RejectModal";
import { UsersWithWithdrawFilterFormPaginated } from "features/withdraw/components/users-with-wallet-filter-form/type";
import { useSuperAdminManual } from "features/withdraw/use-cases/super-admin-manual";
import { Dispatch, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "routes/constants";
import { getRoute } from "routes/utils";
import { UserWithithdraw } from "types/withdraw";
import { DEFAULT_TIMEZONE, formatDate } from "utils/date";

type UsersWithWalletTableProps = {
  isLoading: boolean;
  data?: any;
  tableParams: UsersWithWithdrawFilterFormPaginated;
  updateParams: Dispatch<SetStateAction<UsersWithWithdrawFilterFormPaginated>>;
};

export const SuperAdminTable = ({
  isLoading,
  data,
  tableParams,
  updateParams,
}: UsersWithWalletTableProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const permissions = usePermissions();
  const { user }: any = useUserContext();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [, setTotalSelected] = useState(0);

  const [openApproveModal, setOpenApproveModal] = useState(false);
  const [openRejectModal, setOpenRejectModal] = useState(false);
  const [openRefundModal, setOpenRefundModal] = useState(false);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const { mutate: handleBulkAction, isLoading: isBulkLoading } =
    useSuperAdminManual();

  if (!permissions) return null;

  const handleBulkActionClick = (
    actionType: number,
    remark?: string,
    referenceNumber?: string
  ) => {
    const selectableIds =
      data?.items
        ?.filter((item: any) => item.status_id === 2)
        .map((item: any) => item.id) || [];

    const isAll =
      isAllSelected || selectedRowKeys.length === selectableIds.length;

    const selectedRecords = data?.items?.filter((item: any) =>
      selectedRowKeys.includes(item.id)
    );

    const payload: CreateApprovedDTO = {
      WithdrawRequestIds: isAll
        ? selectedRecords.map((item: any) => item.id)
        : selectedRecords.map((item: any) => item.id),
      CreatedBy: user?.id,
      LoginUserName: user?.user_name,
      Remark: remark || "",
      ActionType: actionType,
      IsAll: isAll,
      RoleType: user?.role,
      ...(actionType === 3 && {
        RefrenceNumber: referenceNumber,
      }),
    };
    handleBulkAction(payload, {
      onSuccess: () => {
        setSelectedRowKeys([]);
        setIsAllSelected(false);
      },
    });
  };
  const rowSelection = {
    selectedRowKeys,

    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys);

      setIsAllSelected(false);
    },

    onSelectAll: (selected: boolean) => {
      setIsAllSelected(selected);

      if (selected) {
        const allIds = data?.items
          ?.filter((item: any) => [2, 10, 11].includes(item.status_id))
          .map((item: any) => item.id);

        setSelectedRowKeys(allIds || []);
        setTotalSelected(allIds?.length || 0);
      } else {
        setSelectedRowKeys([]);
        setTotalSelected(0);
      }
    },

    getCheckboxProps: (record: any) => ({
      disabled: ![2, 10, 11].includes(record.status_id),
    }),
  };

  const isDisabled = selectedRowKeys.length === 0 && !isAllSelected;
  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<UserWithithdraw> | SorterResult<UserWithithdraw>[]
  ) => {
    const sortBy = () => {
      if (Array.isArray(sorter)) return;

      const sortOrder =
        sorter.order === "ascend" ? `${sorter.field}` : `-${sorter.field}`;

      if (sorter?.field === "last_transaction_date") {
        return {
          order_by_balance: undefined,
          order_by_transaction: sortOrder,
        };
      }
    };

    updateParams({
      ...tableParams,
      page: pagination.current,
      size: pagination.pageSize,
      ...sortBy(),
    });
  };
  const handleValue = (matchId: any) => {
    navigate(getRoute(ROUTES.SUPER_ADMIN_APPROVE, matchId));
  };
  const handleValueRejectORManual = (matchId: number, key: any) => {
    if (key == 1) {
      navigate(getRoute(ROUTES.SUPER_ADMIN_REJECT, matchId));
    } else {
      navigate(getRoute(ROUTES.SUPER_ADMIN_MANUAL, matchId));
    }
  };

  const items = (record: any): MenuProps["items"] => {
    return [
      record.status_id == 2
        ? {
            label: "Manual Refund",
            onClick: () => handleValueRejectORManual(record.id, "3"),
            key: "3",
          }
        : null,
      {
        label: "View",
        onClick: () => navigate(getRoute(ROUTES.SUPER_DETAIL, record.id)),
        key: "2",
      },
    ].filter(Boolean); // removes null values
  };

  return (
    <StyledTableWrapper>
      <StyledTableHeader
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
        }}
      >
        <RecordCountBadge text={"Total"} count={data?.total || 0} />
        {selectedRowKeys.length > 0 && (
          <div
            style={{
              display: "flex",
              gap: "10px",
              alignItems: "center",
              lineHeight: "1.5",
            }}
          >
            <Button
              type="primary"
              style={{ backgroundColor: "#53a869" }}
              onClick={() => setOpenApproveModal(true)}
              disabled={isDisabled}
            >
              Bulk Approve
            </Button>
            <Button
              type="primary"
              danger
              style={{
                borderColor: "#ff4d4f",
                color: "#ff4d4f",
                backgroundColor: "transparent",
              }}
              onClick={() => setOpenRejectModal(true)}
            >
              Bulk Reject
            </Button>

            <Button
              type="primary"
              danger
              style={{
                borderColor: "#53a869",
                color: "#53a869",
                backgroundColor: "transparent",
              }}
              onClick={() => setOpenRefundModal(true)}
            >
              Manual Refund
            </Button>

            <div
              className="total-selected-badge"
              style={{ display: "flex", gap: "10px", color: "#12361F" }}
            >
              <span style={{ fontWeight: "500" }}>Total Selected: </span>
              <div className="badge_ttl">
                <span
                  style={{
                    fontWeight: "500",
                    fontSize: "12px",
                    lineHeight: "2",
                  }}
                >
                  {selectedRowKeys.length}
                </span>
              </div>
            </div>
          </div>
        )}
      </StyledTableHeader>

      <Table<UserWithithdraw>
        rowSelection={rowSelection}
        loading={isLoading || isBulkLoading}
        dataSource={data?.items}
        rowKey="id"
        className=""
        onChange={handleTableChange}
        pagination={{
          current: tableParams.page,
          defaultPageSize: tableParams.size,
          total: data?.total,
        }}
        scroll={{ x: 1440 }}
      >
        <Column
          title={t("wallet.table.userName")}
          dataIndex={"user_name"}
          // width={screens.xxl ? 'auto' : 170}
          render={(_, record: any) => record.user_name}
          width={150}
        />
        <Column<UserWithithdraw>
          title={t("wallet.table.phone")}
          dataIndex={"phone_number"}
          // width={screens.xxl ? 'auto' : 170}
        />

        <Column<UserWithithdraw>
          title={t("withdraw.table.amount")}
          dataIndex={"amount"}
          // sorter={true}
          // sortDirections={['descend', 'ascend', 'descend']} // workaround to remove null as an option
          // width={screens.xxl ? 'auto' : 130}
          render={(_, record: any) => (
            <span>{`${record.amount ? `${record.amount} KD` : ""}`}</span>
          )}
        />
        <Column<UserWithithdraw>
          title={t("withdraw.table.Date")}
          dataIndex={"request_date"}
          width={150}
          render={(_, record: any) => (
            <div style={{ whiteSpace: "nowrap" }}>
              {formatDate({
                date: record.request_date,
                format: "date",
                timeZone: DEFAULT_TIMEZONE,
              } as any)}
              <Space style={{ padding: "2px" }}>
                {formatDate({
                  date: record.request_date,
                  format: "time",
                  timeZone: DEFAULT_TIMEZONE,
                } as any)}
              </Space>
            </div>
          )}
          // width={screens.xxl ? 'auto' : 130}
        />

        <Column<UserWithithdraw>
          title={t("withdraw.table.transactionDate")}
          dataIndex={"transaction_date"}
          width={150}
          render={(_, record: any) => (
            <div style={{ whiteSpace: "nowrap" }}>
              {formatDate({
                date: record.transaction_date,
                format: "date",
                timeZone: DEFAULT_TIMEZONE,
              } as any)}
              <Space style={{ padding: "2px" }}>
                {formatDate({
                  date: record.transaction_date,
                  format: "time",
                  timeZone: DEFAULT_TIMEZONE,
                } as any)}
              </Space>
            </div>
          )}
        />
        <Column<UserWithithdraw>
          title={"Payment Method"}
          dataIndex={"payment_method"}
          width={200}
          // width={screens.xxl ? 'auto' : 100}
          render={(_, record: any) => (
            <>
              <span>{`${record.payment_method}`}</span>
            </>
          )}
        />
        <Column<UserWithithdraw>
          title={"Auth Id"}
          dataIndex={"auth"}
          render={(auth) => auth || "-"}
        />
        <Column<UserWithithdraw>
          title="Order Id"
          width={250}
          dataIndex="ottu_order_no"
          render={(ottu_order_no) => ottu_order_no || "-"}
        />
        <Column<UserWithithdraw>
          title={t("withdraw.table.Status")}
          dataIndex={"status_name"}
          width={150}
          // width= '100px'
          render={(_, record: any) => (
            <>
              <span>{`${record.status_name}`}</span>
            </>
          )}
        />
        {/* <Column<UserWithithdraw>
          title={t("withdraw.table.action")}
          dataIndex="last_transaction_date"
          width={200}
          render={(_, record: any) => {
            const isRejected = record.status_id === 5;
            const isApproved = record.status_name === "Approved By SuperAdmin";
            const hideActionButtons = [6, 7, 8, 9].includes(record.status_id);

            const hideApprove =
              record?.payment_method === "APPLEPAY" &&
              ["SuperAdmin", "Li3ibAccountant"].includes(user?.role);

            return (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "15px",
                  minHeight: "40px",
                }}
              >
                {isApproved ? (
                  <Button
                    type="primary"
                    size="middle"
                    disabled
                    style={{ fontSize: "12px", height: "30px" }}
                  >
                    Approved
                  </Button>
                ) : isRejected ? (
                  <Button
                    danger
                    size="middle"
                    disabled
                    style={{ fontSize: "12px", height: "30px" }}
                  >
                    Rejected
                  </Button>
                ) : (
                  <>
                    {!hideApprove && !hideActionButtons && (
                      <Button
                        type="primary"
                        size="middle"
                        style={{
                          fontSize: "12px",
                          height: "30px",
                          minWidth: "80px",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleValue(record.id);
                        }}
                      >
                        Approve
                      </Button>
                    )}

                    {!hideActionButtons && (
                      <Button
                        danger
                        size="middle"
                        style={{
                          fontSize: "12px",
                          height: "30px",
                          minWidth: "80px",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleValueRejectORManual(record.id, "1");
                        }}
                      >
                        Reject
                      </Button>
                    )}
                  </>
                )}
                <Dropdown menu={{ items: items(record) }} trigger={["click"]}>
                  <div
                    onClick={(e) => e.preventDefault()}
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "6px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      border: "1px solid #d9d9d9",
                      background: "#fff",
                    }}
                  >
                    <MoreOutlined
                      style={{
                        fontSize: 18,
                        fontWeight: 600,
                      }}
                    />
                  </div>
                </Dropdown>
              </div>
            );
          }}
        /> */}
        <Column<UserWithithdraw>
          title={t("withdraw.table.action")}
          dataIndex="last_transaction_date"
          width={200}
          render={(_, record: any) => {
            // 1. Check if THIS specific row is selected
            const isThisRowSelected = selectedRowKeys.includes(record.id);

            const isRejected = record.status_id === 5;
            const isApproved = record.status_name === "Approved By SuperAdmin";
            const hideActionButtons = [6, 7, 8, 9].includes(record.status_id);

            const hideApprove =
              record?.payment_method === "APPLEPAY" &&
              ["SuperAdmin", "Li3ibAccountant"].includes(user?.role);

            // 2. If this row's checkbox is checked, hide the actions
            if (isThisRowSelected) {
              return (
                <div
                  style={{
                    minHeight: "40px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <span
                    style={{
                      color: "#bfbfbf",
                      fontStyle: "italic",
                      fontSize: "12px",
                    }}
                  ></span>
                </div>
              );
            }

            return (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "15px",
                  minHeight: "40px",
                }}
              >
                {isApproved ? (
                  <Button
                    type="primary"
                    size="middle"
                    disabled
                    style={{ fontSize: "12px", height: "30px" }}
                  >
                    Approved
                  </Button>
                ) : isRejected ? (
                  <Button
                    danger
                    size="middle"
                    disabled
                    style={{ fontSize: "12px", height: "30px" }}
                  >
                    Rejected
                  </Button>
                ) : (
                  <>
                    {!hideApprove && !hideActionButtons && (
                      <Button
                        type="primary"
                        size="middle"
                        style={{
                          fontSize: "12px",
                          height: "30px",
                          minWidth: "80px",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleValue(record.id);
                        }}
                      >
                        Approve
                      </Button>
                    )}

                    {!hideActionButtons && (
                      <Button
                        danger
                        size="middle"
                        style={{
                          fontSize: "12px",
                          height: "30px",
                          minWidth: "80px",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleValueRejectORManual(record.id, "1");
                        }}
                      >
                        Reject
                      </Button>
                    )}
                  </>
                )}

                <Dropdown menu={{ items: items(record) }} trigger={["click"]}>
                  <div
                    onClick={(e) => e.preventDefault()}
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "6px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      border: "1px solid #d9d9d9",
                      background: "#fff",
                    }}
                  >
                    <MoreOutlined style={{ fontSize: 18, fontWeight: 600 }} />
                  </div>
                </Dropdown>
              </div>
            );
          }}
        />
      </Table>

      <ApproveModal
        open={openApproveModal}
        onCancel={() => setOpenApproveModal(false)}
        onConfirm={(remark: string) => {
          handleBulkActionClick(1, remark); // Approve
          setOpenApproveModal(false);
        }}
      />
      <RejectModal
        open={openRejectModal}
        onCancel={() => setOpenRejectModal(false)}
        onConfirm={(remark: string) => {
          handleBulkActionClick(2, remark); // Approve
          setOpenRejectModal(false);
        }}
      />

      <RefundModal
        open={openRefundModal}
        onCancel={() => setOpenRefundModal(false)}
        onConfirm={(remark: string, referenceNumber: string) => {
          handleBulkActionClick(3, remark, referenceNumber);
          setOpenRefundModal(false);
        }}
      />
    </StyledTableWrapper>
  );
};
